import React from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Checkbox,
  VStack,
  Spinner,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
  MenuItem,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { DarwinHeader } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { HeaderWithStats } from "services/MetadataServices";
import { AppIcons } from "constants/icons";
import { getRelativeTimeString } from "utils/dateUtils";

export interface ChatSessionListProps {
  // Data
  chatSessions: DarwinHeader[];
  totalChats: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  isDeleting: boolean;
  searchQuery: string;
  selectedIds: Set<string>;
  hoveredId: string | null;
  editingId: string | null;
  editValue: string;
  selectedAnalyst: Header | null;
  analystsList: HeaderWithStats[];
  isAllSelected: boolean;
  // Callbacks
  onToggleSelection: (id: string) => void;
  onToggleSelectAll: () => void;
  onDeleteSelected: () => void;
  onAnalystSelect: (analyst: Header | null) => void;
  onChatClick: (id: string) => void;
  onRenameStart: (sessionId: string, name: string) => void;
  onRenameChange: (value: string) => void;
  onRenameSubmit: (sessionId: string) => void;
  onRenameCancel: () => void;
  onDeleteSingle: (sessionId: string) => void;
  onLoadMore: () => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
  onSearchChange: (value: string) => void;
  onNewChat: () => void;
}

const ChatSessionList: React.FC<ChatSessionListProps> = ({
  chatSessions,
  totalChats,
  isLoading,
  isLoadingMore,
  isDeleting,
  searchQuery,
  selectedIds,
  hoveredId,
  editingId,
  editValue,
  selectedAnalyst,
  analystsList,
  isAllSelected,
  onToggleSelection,
  onToggleSelectAll,
  onDeleteSelected,
  onAnalystSelect,
  onChatClick,
  onRenameStart,
  onRenameChange,
  onRenameSubmit,
  onRenameCancel,
  onDeleteSingle,
  onLoadMore,
  onHoverEnter,
  onHoverLeave,
  onSearchChange,
  onNewChat,
}) => {
  return (
    <Box maxW="700px" w="100%" mx="auto" display="flex" flexDirection="column" flex={1} minH={0}>
      {/* Header: title on its own line, then search + new chat */}
      <Box mb={4}>
        <HStack spacing={2} mb={3}>
          <Icon as={AppIcons.chatSessions} boxSize={7} color="purple.700" />
          <Text fontSize="xl" fontWeight="700" color="purple.700" m={0}>
            Chats
          </Text>
        </HStack>
        <Flex gap={3} align="center">
          <InputGroup size="md" flex={1}>
            <InputLeftElement pointerEvents="none" h="40px">
              <Icon as={AppIcons.search} boxSize={4} color="purple.700" />
            </InputLeftElement>
            <Input
              placeholder="Search chats..."
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              bg="white"
              border="1px solid"
              borderColor="purple.800"
              borderRadius="lg"
              fontSize="md"
              color="fg.default"
              _placeholder={{ color: "purple.800" }}
              _focus={{
                borderColor: "purple.700",
                boxShadow: "none",
                borderWidth: "2px",
                bg: "purple.50",
              }}
            />
          </InputGroup>
          <Button
            leftIcon={<Icon as={AppIcons.plus} boxSize={3.5} />}
            bg="purple.700"
            color="white"
            size="md"
            fontWeight="600"
            borderRadius="md"
            flexShrink={0}
            _hover={{ bg: "purple.600" }}
            onClick={onNewChat}
          >
            New chat
          </Button>
        </Flex>
      </Box>

      {/* Selection Actions */}
      {selectedIds.size > 0 && (
        <Flex mb={2} pl={3} pr={1} py={0} bg="gray.100" borderRadius="md" align="center" justify="space-between">
          <Text fontSize="sm" color="fg.default" fontWeight="600" align="left" m={0}>
            {selectedIds.size} chat{selectedIds.size > 1 ? "s" : ""} selected
          </Text>
          <Button
            leftIcon={<Icon as={AppIcons.delete} boxSize={4} />}
            size="xs"
            colorScheme="red"
            variant="ghost"
            isLoading={isDeleting}
            onClick={onDeleteSelected}
            fontWeight="700"
          >
            Delete
          </Button>
        </Flex>
      )}

      {/* Chat Count & Select All & Analyst Filter */}
      <Flex align="center" justify="space-between" mb={3} px={1}>
        <HStack spacing={2}>
          <Checkbox isChecked={isAllSelected} onChange={onToggleSelectAll} colorScheme="gray" size="md" m={0} />
          <Text fontSize="sm" color="gray.500" fontWeight="600" align="left" m={0}>
            Showing {chatSessions.length} of {totalChats} chat{totalChats !== 1 ? "s" : ""}
          </Text>
        </HStack>

        {/* Analyst Filter */}
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            variant="outline"
            colorScheme="purple"
            bg="purple.50"
            borderColor="purple.50"
            _hover={{
              bg: "purple.100",
              borderColor: "purple.100",
            }}
            borderRadius="full"
            borderWidth="1px"
            px={3}
            py={1}
          >
            <HStack spacing={2}>
              <Icon as={AppIcons.analyst} boxSize={5} />
              <Text fontSize="sm" color="fg.default" fontWeight="500" m={0} noOfLines={1}>
                {selectedAnalyst ? selectedAnalyst.name : "All analysts"}
              </Text>
              <Icon as={AppIcons.chevronDown} boxSize={3.5} />
            </HStack>
          </MenuButton>
          <MenuList maxH="250px" overflowY="auto" minW="200px" borderColor="purple.200" boxShadow="lg">
            <MenuItem
              fontSize="sm"
              bg={!selectedAnalyst ? "purple.700" : "transparent"}
              _hover={{ bg: !selectedAnalyst ? "purple.600" : "purple.50" }}
              onClick={() => onAnalystSelect(null)}
            >
              <HStack spacing={2}>
                <Icon as={AppIcons.analyst} boxSize={5} color={!selectedAnalyst ? "white" : "purple.700"} />
                <Text fontSize="sm" fontWeight="500" m={0} color={!selectedAnalyst ? "white" : "fg.default"}>
                  All analysts
                </Text>
              </HStack>
            </MenuItem>
            {analystsList.length > 0 && <MenuDivider />}
            {analystsList.length === 0 ? (
              <MenuItem isDisabled fontSize="sm" color="purple.500">
                No analysts found
              </MenuItem>
            ) : (
              analystsList.map(analyst => (
                <MenuItem
                  key={analyst.id}
                  fontSize="sm"
                  bg={selectedAnalyst?.id === analyst.id ? "purple.700" : "transparent"}
                  _hover={{ bg: selectedAnalyst?.id === analyst.id ? "purple.600" : "purple.50" }}
                  onClick={() => onAnalystSelect(analyst)}
                >
                  <HStack spacing={2}>
                    <Icon
                      as={AppIcons.analyst}
                      boxSize={5}
                      color={selectedAnalyst?.id === analyst.id ? "white" : "purple.700"}
                    />
                    <Text
                      fontSize="sm"
                      fontWeight="500"
                      m={0}
                      noOfLines={1}
                      color={selectedAnalyst?.id === analyst.id ? "white" : "fg.default"}
                    >
                      {analyst.name}
                    </Text>
                  </HStack>
                </MenuItem>
              ))
            )}
          </MenuList>
        </Menu>
      </Flex>

      {/* Chat List */}
      <Box
        minH={0}
        overflowY="auto"
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#dfc2f0",
            borderRadius: "2px",
          },
        }}
        maxW="700px"
        alignItems="center"
      >
        {isLoading ? (
          <Flex justify="center" align="center" h="150px">
            <Spinner size="md" color="purple.700" thickness="2px" />
          </Flex>
        ) : chatSessions.length === 0 ? (
          <Flex direction="column" align="center" justify="center" h="150px" color="gray.500">
            <Text fontSize="md" mb={1}>
              {searchQuery ? "No chats found" : "No chats yet"}
            </Text>
            <Text fontSize="sm">{searchQuery ? "Try a different search term" : "Start a new chat to begin"}</Text>
          </Flex>
        ) : (
          <VStack spacing={2} align="stretch">
            {chatSessions.map(session => {
              const isEditing = editingId === session.id;
              return (
                <Flex
                  key={session.id}
                  p={3}
                  align="center"
                  borderRadius="lg"
                  border={selectedIds.has(session.id!) ? "2px solid" : "1px solid"}
                  borderColor={selectedIds.has(session.id!) ? "gray.500" : "gray.300"}
                  bg={selectedIds.has(session.id!) ? "purple.50" : hoveredId === session.id ? "gray.50" : "white"}
                  cursor={isEditing ? "default" : "pointer"}
                  transition="all 0.15s ease"
                  onMouseEnter={() => onHoverEnter(session.id!)}
                  onMouseLeave={onHoverLeave}
                  onClick={() => !isEditing && onChatClick(session.id!)}
                  role="group"
                  tabIndex={0}
                  _hover={{ borderColor: "gray.500" }}
                >
                  {/* Checkbox */}
                  <Box
                    onClick={e => {
                      e.stopPropagation();
                      onToggleSelection(session.id!);
                    }}
                    mr={3}
                  >
                    <Checkbox
                      isChecked={selectedIds.has(session.id!)}
                      onChange={() => onToggleSelection(session.id!)}
                      colorScheme="purple"
                      size="md"
                    />
                  </Box>

                  {/* Chat Info */}
                  <Box flex="1" minW={0}>
                    {isEditing ? (
                      <Input
                        size="sm"
                        autoFocus
                        value={editValue}
                        onChange={e => onRenameChange(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") onRenameSubmit(session.id!);
                          if (e.key === "Escape") onRenameCancel();
                        }}
                        onBlur={onRenameCancel}
                        onClick={e => e.stopPropagation()}
                        bg="white"
                        color="fg.default"
                        border="1px solid"
                        borderColor="gray.300"
                        borderRadius="md"
                        fontSize="sm"
                      />
                    ) : (
                      <>
                        <Text fontSize="md" fontWeight="600" color="fg.default" noOfLines={1} align="left" m={0}>
                          {session.name || "Untitled"}
                        </Text>
                        <Text fontSize="sm" color="gray.500" align="left" m={0} mt={1}>
                          Last message{" "}
                          {session.last_modified_date ? getRelativeTimeString(session.last_modified_date) : ""}
                        </Text>
                      </>
                    )}
                  </Box>

                  {/* Actions Menu */}
                  {!isEditing && (
                    <Menu placement="bottom-start">
                      <MenuButton
                        as={IconButton}
                        icon={<Icon as={AppIcons.menuDots} boxSize={3.5} />}
                        size="xs"
                        variant="ghost"
                        color="gray.500"
                        _hover={{ bg: "gray.100", color: "gray.700" }}
                        aria-label="Chat options"
                        onClick={e => e.stopPropagation()}
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        transition="opacity 0.2s"
                      />
                      <MenuList minW="120px" boxShadow="lg" zIndex={1001}>
                        <MenuItem
                          icon={<AppIcons.edit size={14} />}
                          fontSize="sm"
                          onClick={e => {
                            e.stopPropagation();
                            onRenameStart(session.id!, session.name || "");
                          }}
                        >
                          Rename
                        </MenuItem>
                        <MenuItem
                          icon={<AppIcons.delete size={12} />}
                          fontSize="sm"
                          color="red.500"
                          onClick={e => {
                            e.stopPropagation();
                            onDeleteSingle(session.id!);
                          }}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Flex>
              );
            })}

            {/* Load More */}
            {chatSessions.length > 0 && chatSessions.length < totalChats && (
              <Button
                variant="outline"
                colorScheme="purple"
                size="sm"
                mt={2}
                isLoading={isLoadingMore}
                loadingText="Loading..."
                onClick={onLoadMore}
              >
                Load More
              </Button>
            )}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default ChatSessionList;
